import { useState } from "react";
import {
  RiFlightTakeoffFill,
  RiFlightLandFill,
  RiArrowRightLine,
  RiCalendar2Line,
  RiTimeLine,
  RiPlaneFill,
  RiGroupLine,
  RiBuildingLine,
  RiArrowDownSLine,
} from "@remixicon/react";
import { Card, CardContent } from "@components/ui/Card";
import StatusBadge from "./StatusBadge";
import { formatDateInAirportTimezone, formatTimeInAirportTimezone } from '@/utils/airportTimezone';
import { getAircraftModelName, getOperatorWithLocation } from '@/features/tails/utils';

const FlightInfoCard = ({ flight, liveData, tailSet }) => {
  const [collapsed, setCollapsed] = useState(false);

  if (!flight) return null;

  const formatDate = (dateString, airportIcao) => {
    if (!dateString) return 'N/A';
    return formatDateInAirportTimezone(dateString, airportIcao, {
      day: 'numeric',
      month: 'short',
    });
  };

  const formatTime = (dateString, airportIcao) => {
    if (!dateString) return 'N/A';
    return formatTimeInAirportTimezone(dateString, airportIcao);
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return null;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  console.log("liveData", liveData);

  const tailNumber = flight?.aircraft?.tail_number || liveData?.reg || 'TBD';
  const aircraftType = getAircraftModelName(flight?.aircraft);
  const operatorInfo = getOperatorWithLocation(flight?.aircraft);

  const depIcao = liveData?.orig_icao || flight.departure_airport;
  const arrIcao = liveData?.dest_icao || flight.arrival_airport;

  // Calculate ETA remaining from eta timestamp
  const etaTimestamp = liveData?.eta ? new Date(liveData.eta) : null;
  const nowTimestamp = liveData?.timestamp ? new Date(liveData.timestamp) : null;
  const etaRemainingSeconds = etaTimestamp && nowTimestamp ? Math.max(0, (etaTimestamp - nowTimestamp) / 1000) : null;

  // Elapsed time since departure
  const depTime = flight.departure_date ? new Date(flight.departure_date) : null;
  const elapsedSeconds = depTime && nowTimestamp ? Math.max(0, (nowTimestamp - depTime) / 1000) : null;

  const progress = liveData?.progress || 0;
  const distFlown = liveData?.distFlown;
  const distRemaining = liveData?.distRemaining;

  // Derive flight status from live data and departure/arrival times
  const flightStatus = (() => {
    if (liveData && liveData.gspeed === 0 && liveData.vspeed === 0) return 'completed';
    const arrDate = flight.arrival_date ? new Date(flight.arrival_date) : null;
    const etaDate = liveData?.eta ? new Date(liveData.eta) : null;
    const arrivalTime = etaDate || arrDate;
    if (arrivalTime && arrivalTime < new Date() && !(liveData && liveData.lat != null)) return 'completed';
    if (liveData && liveData.lat != null) return 'in_flight';
    const depTime = flight.departure_date ? new Date(flight.departure_date) : null;
    if (depTime && depTime < new Date()) return 'delayed';
    return 'pending';
  })();

  return (
    <div className="w-full md:w-[420px] md:ml-5 bg-white/40 backdrop-blur-md rounded-2xl shadow-xl p-4 md:p-5">
      {/* Client name + status - outside card */}
      <div
        className="flex items-center justify-between mb-0 md:mb-3 cursor-pointer md:cursor-default"
        onClick={() => setCollapsed((prev) => !prev)}
      >
        <p className="text-lg md:text-2xl font-semibold text-gray-900">
          {flight.quote?.client?.first_name} {flight.quote?.client?.last_name}
        </p>
        <div className="flex items-center gap-2">
          <StatusBadge status={flightStatus} />
          <RiArrowDownSLine className={`w-5 h-5 text-gray-400 md:hidden transition-transform ${collapsed ? '-rotate-90' : ''}`} />
        </div>
      </div>

      <div className={`${collapsed ? 'hidden' : 'block'} mt-3`}>
      <Card className="!rounded-2xl !bg-white/80 !shadow-none !border-0">
        <CardContent className="p-4">
        {/* Tail number */}
        <div className="mb-1">
          <h3 className="text-lg font-bold text-gray-900">{tailSet ? tailSet : tailNumber}</h3>
        </div>

        {/* Aircraft type + tracked by */}
        <div className="flex items-center justify-between mb-1">

            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <RiPlaneFill className="w-3.5 h-3.5" />
              <span>{tailSet ? "-" : aircraftType || ""}</span>
            </div>

            {liveData && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-light border border-blue-light px-2.5 py-0.5 text-xs font-medium text-blue-dark">
                Tracked by {liveData.track}
                <RiGroupLine className="w-3 h-3" />
              </span>
            )}
    
        </div>

        {/* Operator */}
        <div className="flex items-center gap-1.5 mb-2 text-sm text-gray-500">
          <RiBuildingLine className="w-3.5 h-3.5" />
          <span>{tailSet ? "-" : operatorInfo}</span>
        </div>

        {/* Route */}
        <div className="flex items-center gap-1 mb-4">
          <div className="flex-1 flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5">
            <RiFlightTakeoffFill className="w-4 h-4 text-gray-600 flex-shrink-0" />
            <span className="text-sm font-medium truncate">
              {depIcao}
            </span>
          </div>
          <RiArrowRightLine className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="flex-1 flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5">
            <RiFlightLandFill className="w-4 h-4 text-gray-600 flex-shrink-0" />
            <span className="text-sm font-medium truncate">
              {arrIcao}
            </span>
          </div>
        </div>

        {/* Date & time - two columns */}
        <div className="flex justify-between mb-4">
          {/* Departure */}
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <RiCalendar2Line className="w-4 h-4" />
              <span>{formatDate(flight.departure_date, depIcao)}</span>
            </div>
            <div className="flex items-center gap-1">
              <RiTimeLine className="w-4 h-4" />
              <span>{formatTime(flight.departure_date, depIcao)}</span>
            </div>
          </div>
          {/* Arrival */}
          {(liveData?.eta || flight.arrival_date) && (
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <RiCalendar2Line className="w-4 h-4" />
                <span>{formatDate(liveData?.eta || flight.arrival_date, arrIcao)}</span>
              </div>
              <div className="flex items-center gap-1">
                <RiTimeLine className="w-4 h-4" />
                <span>{formatTime(liveData?.eta || flight.arrival_date, arrIcao)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Progress bar */}

        <div>
          <div className="relative w-full bg-gray-200 rounded-full h-1.5">
            <div
              className={`${flightStatus === 'completed' ? 'bg-green-500' : 'bg-amber-400'} h-1.5 rounded-full transition-all`}
              style={{ width: `${progress}%` }}
            />

              <div
                className="absolute -top-2.5 transition-all"
                style={{ left: `${progress}%`, transform: 'translateX(-50%)' }}
              >
                <RiPlaneFill className="w-6 h-6 text-gray-700 rotate-90" />
              </div>

          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>
              {distFlown != null ? `${Math.round(distFlown).toLocaleString()} km` : depIcao}
              {elapsedSeconds != null ? `, ${formatDuration(elapsedSeconds)} ago` : ''}
            </span>
            <span>
              {distRemaining != null ? `${Math.round(distRemaining).toLocaleString()} km` : arrIcao}
              {etaRemainingSeconds != null ? `, in ${formatDuration(etaRemainingSeconds)}` : ''}
            </span>
          </div>
        </div>

      </CardContent>
    </Card>
    </div>
    </div>
  );
};

export default FlightInfoCard;
